import {
  AUTO_UPDATE_LATEST,
  CUSTOM_SUPPORT_UPDATE_PATH,
  CUSTOM_UPDATE_PATH,
  DAY_IN_SECONDS,
  DEVICES_WITH_ADDITIONAL_SW_UPDATE_PATHS,
  DEVICE_PARAM_TYPE,
  HOUR_IN_SECONDS,
  INHERITANCE_ORDER,
  LATEST,
  MAIN,
  MINUTE_IN_SECONDS,
  PARAM_TYPE,
  POLLING_INTERVAL,
  SCHEDULE_UPDATE_CHECKS,
  SOFTWARE_UPDATE,
  SUPPORT_UPDATE_PATH,
  UPDATE_PATH,
  UPDATE_SOURCE,
  UPDATE_START_TIME,
  UPDATE_STOP_TIME
} from "Constants";
import { isProvisionedDevice } from "elements/ConnectionStatusIndicator/ConnectionStatusIndicator";
import {
  DeviceFragment,
  EnumVal,
  ExtendedDeviceParameterFragment,
  FeatureCategory,
  GroupFragment,
  Maybe,
  PoliciesQueryVariables,
  PolicyDeploymentScope,
  PolicyFragment,
  PolicyType,
  SoftwareSupportUpdatePath,
  SwSupportUpdatePathFragment,
  SwUpdateSource,
  UpdateImageFragment,
  useDeviceGroupsQuery,
  useEpUpdateImagesQuery,
  useGroupsForDeviceQuery,
  useHardwareModelsQuery,
  usePoliciesQuery,
  HardwareModelFragment
} from "generated/graphql";
import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { provisionedDeviceSettingInheritance } from "store/provisionedDeviceSettingsInheritance";
import i18next from "utilities/i18n";
import {
  findCatalogModel,
  isDefaultProvisionedDevice,
  isPolyOS
} from "utilities/modelSelectors";
import { SectionSetting } from "../ProvisionedDeviceSettings";
import { useProvisionedDevicePolicyScopeContext } from "../ProvisionedDeviceSettingsRouter";
import { ProvisionedUpdateFormType } from "../ProvisionedUpdateSoftware";
import {
  getDeviceModel,
  getDeviceSettingName,
  settingsNameMap
} from "../utils/provisionedDeviceSettingsUtils";
import {
  SettingValue,
  SwSupportUpdatePath
} from "./useProvisionedDeviceSettings";

type UsePolicyProps = {
  model: string;
  tenantId: string;
  msTeamsAssistedProvisioningEnabled: boolean;
  device?: DeviceFragment;
  siteId?: string;
  groupId?: string;
};
type UseSoftwarePolicyProps = UsePolicyProps & {
  deviceParameters?: Maybe<ExtendedDeviceParameterFragment>[];
};
type UseSettingsPolicyProps = UsePolicyProps;

type UseRelevantPoliciesProps = Pick<
  UsePolicyProps,
  "device" | "siteId" | "msTeamsAssistedProvisioningEnabled"
> & {
  type: PolicyType;
  policyScope: PolicyDeploymentScope;
  deviceModel: string;
  tenantId: string;
  groupIds: Maybe<string>[];
};
type UseRelevantGroupIdsProps = Pick<UsePolicyProps, "groupId" | "device"> & {
  tenantId: string;
};

type UseInheritanceProps = Pick<UsePolicyProps, "device"> & {
  policyScope: PolicyDeploymentScope;
  deviceName: string;
  siteName: string;
  groupIds: Maybe<string>[];
  relevantPolicies: Maybe<PolicyFragment>[];
  allGroups: GroupFragment[];
};

type UseDispatchInheritanceProps = {
  inheritance: InheritanceDetails;
  catalogModel?: Pick<HardwareModelFragment, "id" | "name">;
};

type UseSwUpdateValuesAndSettingsProps = Pick<
  UseSoftwarePolicyProps,
  "deviceParameters"
> & {
  deviceModel: string;
  policyScope: PolicyDeploymentScope;
  policy: PolicyFragment;
  inheritance: InheritanceDetails;
};

type UseCatalogModelProps = Pick<UsePolicyProps, "model" | "device">;

type InheritanceDetails = {
  [name: string]: {
    value: SettingValue;
    scope: PolicyDeploymentScope | null;
    id: string;
    groupPriorityIndex?: number;
  };
};
export type ProvisionedUpdateSetting = SectionSetting & {
  onChange?: {
    settingName: string;
    updater: (value: SettingValue) => SettingValue;
  }[];
};
type VersionOptions = Array<
  EnumVal & {
    swSupportUpdatePath: SwSupportUpdatePath[];
  }
>;

type SettingWithScope = {
  name: string; //name of setting
  value: SettingValue; // scope value, i.e siteName, groupName
  id: string; // scope id, i.e. siteId
  scope: PolicyDeploymentScope;
};

const updateSourceType: Array<EnumVal> = [
  {
    value: SwUpdateSource.StandardUrl,
    friendlyValue: i18next.t("POLY_LENS"),
    featureCategory: FeatureCategory.Released,
    __typename: "EnumVal"
  },
  {
    value: SwUpdateSource.CustomUrl,
    friendlyValue: i18next.t("CUSTOM_SERVER"),
    featureCategory: FeatureCategory.Released,
    __typename: "EnumVal"
  }
];

const extractScopeDetails = (
  policy: PolicyFragment,
  deviceName: string,
  groupData: GroupFragment[],
  siteName: string
) => {
  switch (policy.policyDeploymentScope) {
    case PolicyDeploymentScope.Device:
      return {
        scopeId: policy.deviceId,
        scopeValue: deviceName
      };
    case PolicyDeploymentScope.DeviceModel:
      return {
        scopeId: policy.deviceModelUuid || policy.deviceModel,
        scopeValue: policy.deviceModel
      };
    case PolicyDeploymentScope.Group:
      const groupName = groupData.find(group => group.id === policy.groupId)
        ?.name;
      return {
        scopeId: policy.groupId,
        scopeValue: groupName
      };
    case PolicyDeploymentScope.Site:
      return {
        scopeId: policy.siteId,
        scopeValue: siteName
      };
    case PolicyDeploymentScope.Global:
      return {
        scopeId: policy.deviceId,
        scopeValue: PolicyDeploymentScope.Global
      };
    case PolicyDeploymentScope.ExternalDevice:
      return {
        scopeId: policy.deviceId,
        scopeValue: PolicyDeploymentScope.ExternalDevice
      };
    default:
      return {
        scopeId: undefined,
        scopeValue: undefined
      };
  }
};

const constructPolicyName = (
  model: string,
  type: PolicyType,
  scope: PolicyDeploymentScope,
  id?: string,
  siteId?: string,
  groupId?: string
) => {
  let policyScope: string = "Unknown";
  let identifier: string | undefined = "Unknown";
  switch (scope) {
    case PolicyDeploymentScope.Device:
      policyScope = i18next.t("DEVICE");
      identifier = id;
      break;
    case PolicyDeploymentScope.Site:
      policyScope = i18next.t("SITE");
      identifier = siteId;
      break;
    case PolicyDeploymentScope.Group:
      policyScope = i18next.t("GROUP");
      identifier = groupId;
      break;
    case PolicyDeploymentScope.DeviceModel:
      policyScope = i18next.t("DEVICE_MODEL");
      break;
  }
  const policyType =
    type === PolicyType.DeviceSettingsUpdate
      ? i18next.t("SETTINGS")
      : i18next.t("UPDATE");

  if (scope === PolicyDeploymentScope.DeviceModel) {
    return i18next.t("POLICY_NAME_MODEL", { model, policyScope, policyType });
  }

  return i18next.t("POLICY_NAME", {
    model,
    policyScope,
    identifier,
    policyType
  });
};

const constructCustomSwSupportUpdatePath = (
  model: string
): SoftwareSupportUpdatePath[] => {
  const additionalUpdatePathsToDisplay = DEVICES_WITH_ADDITIONAL_SW_UPDATE_PATHS.filter(
    ({ models }) => models.includes(model)
  )[0]?.additionalUpdatePathsToDisplay;

  return (
    additionalUpdatePathsToDisplay
      ?.filter(deviceModel => deviceModel !== model)
      .map(deviceModel => ({
        __typename: "SoftwareSupportUpdatePath" as const,
        deviceModel,
        swPath: ""
      })) ?? []
  );
};

const constructNewPolicy = (
  deviceModel: string,
  type: PolicyType,
  scope: PolicyDeploymentScope,
  tenantId: string,
  deviceId?: string,
  siteId?: string,
  groupId?: string
): PolicyFragment => ({
  __typename: "Policy",
  policyId: null,
  name: constructPolicyName(
    deviceModel,
    type,
    scope,
    deviceId,
    siteId,
    groupId
  ),
  tenantId,
  deviceId:
    scope === PolicyDeploymentScope.Device && deviceId ? deviceId : null,
  siteId: scope === PolicyDeploymentScope.Site && siteId ? siteId : null,
  groupId: scope === PolicyDeploymentScope.Group && groupId ? groupId : null,
  deviceModel,
  deviceModelUuid: null,
  deviceType: deviceModel,
  policyType: type,
  policyDeploymentScope: scope,
  configurationAttributes: null,
  swUpdatePath: "",
  customSwUpdatePath: "",
  customSwSupportUpdatePath:
    type === PolicyType.DeviceSwUpdate
      ? constructCustomSwSupportUpdatePath(deviceModel)
      : null,
  swUpdateSource: SwUpdateSource.StandardUrl
});

const populateDeviceModelUuid = (
  policy: PolicyFragment,
  policyScope: PolicyDeploymentScope,
  device?: DeviceFragment,
  catalogModelId?: string
) => {
  // set deviceModelUuid for new policies and for those stored without a value
  if (!policy.deviceModelUuid) {
    policy.deviceModelUuid =
      (policyScope === PolicyDeploymentScope.Device
        ? device?.id
        : catalogModelId) ?? null;
  }
};

const getSoftwareRelatedSettings = (
  deviceParameters: Maybe<ExtendedDeviceParameterFragment>[],
  isDefaultProvisioned: boolean
): Record<string, SectionSetting> => {
  if (!deviceParameters) {
    return {};
  }
  const AVAILABLE_SETTINGS = (isDefaultProvisioned
    ? [POLLING_INTERVAL]
    : [
        SCHEDULE_UPDATE_CHECKS,
        UPDATE_START_TIME,
        UPDATE_STOP_TIME,
        POLLING_INTERVAL
      ]
  ).map(name => getDeviceSettingName(name));

  let softwareRelatedSettings: Record<string, SectionSetting> = {};
  deviceParameters
    .filter(
      (param): param is ExtendedDeviceParameterFragment =>
        param !== null &&
        param.name !== null &&
        param.metadata !== null &&
        AVAILABLE_SETTINGS.includes(param.name)
    )
    .forEach(param => {
      const settingName = Object.keys(settingsNameMap).find(
        key => settingsNameMap[key] === param.name
      );
      if (settingName) {
        softwareRelatedSettings[settingName] = {
          ...param.metadata!,
          settingName,
          category: SOFTWARE_UPDATE,
          subcategory: SOFTWARE_UPDATE,
          collection: MAIN,
          value: param.policyAttribute?.currentValue ?? "",
          policyDeploymentScope: param.policyDeploymentScope,
          show: true
        };
      }
    });

  return softwareRelatedSettings;
};

const getFormSettingData = ({
  name,
  friendlyName,
  type,
  value,
  defaultValue = "",
  options,
  scope,
  model
}: {
  name: string;
  friendlyName: string;
  type: string;
  value: SettingValue;
  defaultValue?: string;
  options?: Array<EnumVal>;
  scope?: PolicyDeploymentScope | null;
  model: string;
}): SectionSetting => ({
  displayName: model,
  modelName: model,
  name,
  settingName: name,
  friendlyName,
  paramType: PARAM_TYPE.PARAM,
  type,
  value,
  defaultValue,
  policyDeploymentScope: scope || null,
  help: "",
  templates: "",
  callback: null,
  accessType: null,
  categories: [SOFTWARE_UPDATE, SOFTWARE_UPDATE],
  category: SOFTWARE_UPDATE,
  subcategory: SOFTWARE_UPDATE,
  grouping: null,
  groupingOrder: null,
  collection: MAIN,
  lensUserType: 1,
  lensConditional: null,
  lensConditionalValue: null,
  languageKey: "",
  min: 0,
  max: 0,
  enumVal: options || null,
  numReplace1Max: 0,
  numReplace2Max: 0,
  param2DArray: null,
  paramArray: null,
  featureCategory: FeatureCategory.Released,
  show: true,
  __typename: "DeviceParameterDTO"
});

const getUpdatePath = (
  inheritedUpdatePath: string | undefined,
  policyUpdatePath: string | null,
  versionOptions: VersionOptions
) => {
  let swUpdatePath = (inheritedUpdatePath || policyUpdatePath) ?? "";
  const autoUpdateLatest = swUpdatePath === LATEST;
  if (swUpdatePath && !versionOptions.some(opt => opt.value === swUpdatePath)) {
    swUpdatePath = "";
  }
  const swSupportUpdatePath =
    versionOptions.find(option => option.value === swUpdatePath)
      ?.swSupportUpdatePath ?? [];

  return {
    swUpdatePath,
    swSupportUpdatePath,
    autoUpdateLatest
  };
};

const getVersionOptions = (
  images: Maybe<UpdateImageFragment>[],
  deviceModel: string
): VersionOptions =>
  images.map(image => {
    let updatePath = image?.directory || "";
    if (!isEmpty(image?.swSupportUpdatePath)) {
      const { swPath } =
        image?.swSupportUpdatePath?.find(
          updatePath => updatePath?.deviceModel === deviceModel
        ) ?? {};
      if (swPath) {
        updatePath += `/${swPath}`;
      }
    }

    return {
      friendlyValue: image?.imageVersion || "",
      value: updatePath,
      swSupportUpdatePath:
        image?.swSupportUpdatePath
          ?.filter(
            (updatePath): updatePath is SwSupportUpdatePathFragment =>
              !!updatePath && updatePath.deviceModel !== deviceModel
          )
          .map(({ deviceModel, swPath }) => ({
            deviceModel,
            swPath: `${image.directory}/${swPath}`
          })) || [],
      featureCategory: FeatureCategory.Released,
      __typename: "EnumVal"
    };
  });

const getCustomSwSupportUpdatePath = (
  inheritance: InheritanceDetails,
  policy: PolicyFragment,
  deviceModel: string
) => {
  let customSwSupportUpdatePath: SwSupportUpdatePath[] | undefined = inheritance
    ?.customSwSupportUpdatePath?.value as SwSupportUpdatePath[];
  if (isEmpty(customSwSupportUpdatePath)) {
    customSwSupportUpdatePath = policy.customSwSupportUpdatePath?.filter(
      (update): update is SoftwareSupportUpdatePath => !!update?.deviceModel
    );
  }
  if (isEmpty(customSwSupportUpdatePath)) {
    customSwSupportUpdatePath = constructCustomSwSupportUpdatePath(deviceModel);
  }

  return customSwSupportUpdatePath;
};

const priorityFromPolicy = (
  policy: PolicyFragment,
  relevantGroupIds: (Maybe<string> | undefined)[]
): number => {
  let priority = INHERITANCE_ORDER[PolicyDeploymentScope.Global]; // default to lowest priority
  if (policy.policyDeploymentScope) {
    priority = INHERITANCE_ORDER[policy.policyDeploymentScope];
    if (
      policy.policyDeploymentScope === PolicyDeploymentScope.Group &&
      policy.groupId
    ) {
      const groupPriorityIndex =
        relevantGroupIds.length - relevantGroupIds.indexOf(policy.groupId);
      priority = priority + groupPriorityIndex / 10; //prioritizing groups by utilizing 10th decimal place. e.g. top priority group = 3.1
    }
  }
  return priority;
};

const updateInheritance = (
  inheritance: InheritanceDetails,
  policy: Maybe<PolicyFragment>,
  policyScope: PolicyDeploymentScope,
  deviceName: string,
  siteName: string,
  groupIds: Maybe<string>[],
  allGroups: GroupFragment[]
) => {
  if (!policy?.policyDeploymentScope) {
    return;
  }

  const { scopeValue, scopeId } = extractScopeDetails(
    policy,
    deviceName,
    allGroups,
    siteName
  );

  // find out for which settings we need to calculate inheritance
  let settingsWithScope: SettingWithScope[] = (
    policy.configurationAttributes || []
  ).map(attribute => ({
    name: attribute?.name || "", //name of setting
    value: scopeValue || "", // scope value, i.e siteName, groupName
    id: scopeId || "", // scope id, i.e. siteId
    scope: policy.policyDeploymentScope!
  }));
  if (
    policy.policyType === PolicyType.DeviceSwUpdate &&
    (policy.policyDeploymentScope === policyScope ||
      policyScope === PolicyDeploymentScope.Device)
  ) {
    settingsWithScope = [
      {
        name: UPDATE_SOURCE,
        value: policy[UPDATE_SOURCE]?.toString() || "",
        id: scopeId || "",
        scope: policy.policyDeploymentScope
      },
      {
        name: UPDATE_PATH,
        value: policy[UPDATE_PATH] || "",
        id: scopeId || "",
        scope: policy.policyDeploymentScope
      },
      {
        name: CUSTOM_UPDATE_PATH,
        value: policy[CUSTOM_UPDATE_PATH] || "",
        id: scopeId || "",
        scope: policy.policyDeploymentScope
      },
      {
        name: CUSTOM_SUPPORT_UPDATE_PATH,
        value:
          policy[CUSTOM_SUPPORT_UPDATE_PATH]?.filter(
            (swUpdate): swUpdate is SoftwareSupportUpdatePath => !!swUpdate
          ) || [],
        id: scopeId || "",
        scope: policy.policyDeploymentScope
      }
    ];
  }

  // for each setting, update inheritance details if value is coming from policy with higher priority
  // INHERITANCE_ORDER holds the priorities of policies
  const level = INHERITANCE_ORDER[policy.policyDeploymentScope] ?? 0;
  settingsWithScope.forEach(({ name, scope, id, value }) => {
    const inheritanceScope = inheritance[name]?.scope;
    const currentLevel = inheritanceScope
      ? INHERITANCE_ORDER[inheritanceScope] ?? 0
      : 0;
    const existingGroupPriorityIndex = inheritance[name]?.groupPriorityIndex;
    const groupPriorityIndex = groupIds.indexOf(policy.groupId);
    if (currentLevel < level) {
      inheritance[name] = {
        value,
        scope,
        id
      };
    }
    // if it's a group policy, add in priority index
    if (
      level === INHERITANCE_ORDER.GROUP &&
      currentLevel <= level &&
      (existingGroupPriorityIndex === undefined ||
        (existingGroupPriorityIndex &&
          groupPriorityIndex < existingGroupPriorityIndex))
    ) {
      inheritance[name] = {
        value,
        scope,
        groupPriorityIndex,
        id
      };
    }
  });
};

const useInheritance = ({
  policyScope,
  deviceName,
  siteName,
  groupIds,
  relevantPolicies,
  allGroups
}: UseInheritanceProps) => {
  return useMemo(() => {
    let inheritance: InheritanceDetails = {};
    relevantPolicies.forEach(policy =>
      updateInheritance(
        inheritance,
        policy,
        policyScope,
        deviceName,
        siteName,
        groupIds,
        allGroups
      )
    );
    return inheritance;
  }, [
    allGroups,
    deviceName,
    groupIds,
    relevantPolicies,
    siteName,
    policyScope
  ]);
};

const useSwUpdateInheritance = ({
  policyScope,
  deviceName,
  siteName,
  groupIds,
  relevantPolicies,
  allGroups
}: UseInheritanceProps) => {
  const inheritance = useInheritance({
    policyScope,
    deviceName,
    siteName,
    groupIds,
    relevantPolicies,
    allGroups
  });

  const swUpdateInheritanceForDisplay = useMemo(() => {
    let swUpdateInheritanceForDisplay: InheritanceDetails = {};

    relevantPolicies.forEach(policy => {
      if (!policy?.policyDeploymentScope) {
        return;
      }
      const { scopeValue, scopeId } = extractScopeDetails(
        policy,
        deviceName,
        allGroups,
        siteName
      );

      // This is for the inheritance display
      // The values are the "scope values", i.e. "Santa Cruz Site"
      const settingsWithScope: SettingWithScope[] = [
        {
          name: AUTO_UPDATE_LATEST,
          value: scopeValue || "",
          id: scopeId || "",
          scope: policy.policyDeploymentScope
        },
        {
          name: UPDATE_SOURCE,
          value: scopeValue || "",
          id: scopeId || "",
          scope: policy.policyDeploymentScope
        },
        {
          name: UPDATE_PATH,
          value: scopeValue || "",
          id: scopeId || "",
          scope: policy.policyDeploymentScope
        },
        {
          name: CUSTOM_UPDATE_PATH,
          value: scopeValue || "",
          id: scopeId || "",
          scope: policy.policyDeploymentScope
        },
        {
          name: CUSTOM_SUPPORT_UPDATE_PATH,
          value: scopeValue || "",
          id: scopeId || "",
          scope: policy.policyDeploymentScope
        }
      ];

      settingsWithScope.forEach(({ name, scope, value, id }) => {
        swUpdateInheritanceForDisplay[name] = {
          value,
          scope,
          id
        };
      });
    });

    return swUpdateInheritanceForDisplay;
  }, [allGroups, relevantPolicies, deviceName, siteName]);

  return {
    inheritance,
    inheritanceForDisplay: { ...inheritance, ...swUpdateInheritanceForDisplay }
  };
};

const useDispatchInheritance = ({
  catalogModel,
  inheritance
}: UseDispatchInheritanceProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // set the scope's hardwareModel so we can access at any level
    dispatch(
      provisionedDeviceSettingInheritance({
        key: "hardwareModel",
        id: catalogModel?.id || "",
        value: catalogModel?.name || "",
        scope: PolicyDeploymentScope.Device
      })
    );

    Object.keys(inheritance).forEach(name => {
      if (inheritance[name].value) {
        dispatch(
          provisionedDeviceSettingInheritance({
            key: name.toLocaleLowerCase(),
            id: inheritance[name].id,
            value: String(inheritance[name].value),
            scope: inheritance[name].scope!
          })
        );
      }
    });
  }, [dispatch, inheritance, catalogModel]);
};

const useSwUpdateValuesAndSettings = ({
  deviceModel,
  deviceParameters,
  policyScope,
  policy,
  inheritance
}: UseSwUpdateValuesAndSettingsProps) => {
  const { data, fetching } = useEpUpdateImagesQuery({
    variables: { deviceModel, isGlobal: true }
  });

  const isDefaultProvisioned = isDefaultProvisionedDevice(deviceModel);
  const hasAdditionalSettings =
    (isPolyOS(deviceModel) || isDefaultProvisioned) && !!deviceParameters;
  const { updateValues, updateSettings } = useMemo(() => {
    let updateValues: ProvisionedUpdateFormType = {
      [AUTO_UPDATE_LATEST]: true,
      [UPDATE_SOURCE]: SwUpdateSource.StandardUrl,
      [UPDATE_PATH]: "",
      [SUPPORT_UPDATE_PATH]: [],
      [CUSTOM_UPDATE_PATH]: "",
      [CUSTOM_SUPPORT_UPDATE_PATH]: [],
      [POLLING_INTERVAL]: isDefaultProvisioned
        ? DAY_IN_SECONDS
        : policyScope === PolicyDeploymentScope.Device
        ? 5 * MINUTE_IN_SECONDS
        : HOUR_IN_SECONDS,
      [SCHEDULE_UPDATE_CHECKS]: false,
      [UPDATE_START_TIME]: "0:00",
      [UPDATE_STOP_TIME]: "0:00"
    };
    let updateSettings: Record<string, ProvisionedUpdateSetting> = {};

    if (!policy) {
      return { updateValues, updateSettings };
    }

    const versionOptions = getVersionOptions(
      data?.getEpUpdateImages ?? [],
      deviceModel
    );
    const customSwSupportUpdatePath = getCustomSwSupportUpdatePath(
      inheritance,
      policy,
      deviceModel
    );

    const {
      autoUpdateLatest,
      swUpdatePath,
      swSupportUpdatePath
    } = getUpdatePath(
      inheritance?.swUpdatePath?.value.toString(),
      policy.swUpdatePath,
      versionOptions
    );

    updateValues = {
      ...updateValues,
      [AUTO_UPDATE_LATEST]: autoUpdateLatest,
      [UPDATE_SOURCE]:
        ((inheritance?.swUpdateSource?.value as SwUpdateSource) ||
          policy.swUpdateSource) ??
        SwUpdateSource.StandardUrl,
      [UPDATE_PATH]: swUpdatePath,
      [SUPPORT_UPDATE_PATH]: swSupportUpdatePath,
      [CUSTOM_UPDATE_PATH]:
        (inheritance?.customSwUpdatePath?.value.toString() ||
          policy.customSwUpdatePath) ??
        "",
      [CUSTOM_SUPPORT_UPDATE_PATH]: customSwSupportUpdatePath ?? []
    };

    updateSettings = {
      ...updateSettings,
      autoUpdateLatest: getFormSettingData({
        name: AUTO_UPDATE_LATEST,
        friendlyName: i18next.t("AUTO_UPDATE_LATEST"),
        type: DEVICE_PARAM_TYPE.BOOL,
        value: updateValues[AUTO_UPDATE_LATEST],
        scope: inheritance?.swUpdatePath?.scope,
        model: deviceModel
      }),
      swUpdateSource: getFormSettingData({
        name: UPDATE_SOURCE,
        friendlyName: i18next.t("DOWNLOAD_SOURCE"),
        type: DEVICE_PARAM_TYPE.ENUM,
        options: updateSourceType,
        value: updateValues[UPDATE_SOURCE],
        defaultValue: updateSourceType[0].friendlyValue ?? "",
        scope: inheritance?.swUpdateSource?.scope,
        model: deviceModel
      }),
      swUpdatePath: {
        ...getFormSettingData({
          name: UPDATE_PATH,
          friendlyName: i18next.t("SELECT_VERSION"),
          type: DEVICE_PARAM_TYPE.ENUM,
          options: versionOptions,
          value: updateValues[UPDATE_PATH],
          scope: inheritance?.swUpdatePath?.scope,
          model: deviceModel
        }),
        onChange: [
          {
            settingName: SUPPORT_UPDATE_PATH,
            updater: (value: SettingValue) =>
              versionOptions.find(option => option.value === value)
                ?.swSupportUpdatePath || []
          }
        ]
      },
      customSwUpdatePath: getFormSettingData({
        name: CUSTOM_UPDATE_PATH,
        friendlyName: i18next.t("UPDATE_SERVER_ADDRESS"),
        type: DEVICE_PARAM_TYPE.STRING,
        value: updateValues[CUSTOM_UPDATE_PATH],
        scope: inheritance?.customSwUpdatePath?.scope,
        model: deviceModel
      }),
      customSwSupportUpdatePath: getFormSettingData({
        name: CUSTOM_SUPPORT_UPDATE_PATH,
        friendlyName: i18next.t("ADDITIONAL_UPDATE_SERVER_ADDRESSES"),
        type: DEVICE_PARAM_TYPE.STRING,
        value: updateValues[
          CUSTOM_SUPPORT_UPDATE_PATH
        ] as SwSupportUpdatePath[],
        scope: inheritance?.customSwSupportUpdatePath?.scope,
        model: deviceModel
      })
    };

    if (hasAdditionalSettings) {
      const swRelatedSettings = getSoftwareRelatedSettings(
        deviceParameters ?? [],
        isDefaultProvisioned
      );

      const upgradePollingInterval = Number(
        swRelatedSettings[POLLING_INTERVAL]?.value
      );
      const upgradeTimeFrameEnable =
        swRelatedSettings[SCHEDULE_UPDATE_CHECKS]?.value === "True";
      const upgradeTimeFrameStart = swRelatedSettings[
        UPDATE_START_TIME
      ]?.value.toString();
      const upgradeTimeFrameStop = swRelatedSettings[
        UPDATE_STOP_TIME
      ]?.value.toString();

      updateValues = {
        ...updateValues,
        [SCHEDULE_UPDATE_CHECKS]: upgradeTimeFrameEnable,
        ...(upgradePollingInterval && {
          [POLLING_INTERVAL]: upgradePollingInterval
        }),
        ...(upgradeTimeFrameStart && {
          [UPDATE_START_TIME]: upgradeTimeFrameStart
        }),
        ...(upgradeTimeFrameStop && {
          [UPDATE_STOP_TIME]: upgradeTimeFrameStop
        })
      };

      updateSettings = {
        ...updateSettings,
        ...swRelatedSettings
      };
    }

    return { updateValues, updateSettings };
  }, [
    deviceModel,
    deviceParameters,
    inheritance,
    policy,
    policyScope,
    data?.getEpUpdateImages,
    hasAdditionalSettings,
    isDefaultProvisioned
  ]);

  return { updateValues, updateSettings, fetching };
};

const useRelevantGroupIds = ({
  device,
  groupId,
  tenantId
}: UseRelevantGroupIdsProps) => {
  // if device is provisioned, we need to find out which groups it belongs to
  // so we can calculate inheritance appropriately
  const isProvisioned = isProvisionedDevice(
    device?.hardwareModel,
    device?.lastConfigRequestDate
  );
  const { data, fetching } = useGroupsForDeviceQuery({
    variables: {
      tenantId,
      serial: device?.serialNumber ?? ""
    },
    pause: !isProvisioned || !device?.serialNumber
  });

  const relevantGroupIds = useMemo(() => {
    let relevantGroupIds: Maybe<string>[] = [];
    // If looking at an individual device, relevantGroupIds is an array of groupIds to which the device belongs
    // If looking at a group policy, the only relevant groupId is the one in view
    // If looking at a site policy, no groups are relevant
    if (device?.serialNumber && data?.getDevice?.groups?.length) {
      relevantGroupIds = data.getDevice.groups;
    } else if (groupId) {
      relevantGroupIds = [groupId];
    }
    return relevantGroupIds;
  }, [data?.getDevice, device?.serialNumber, groupId]);

  return { relevantGroupIds, fetching };
};

const useRelevantPolicies = ({
  type,
  policyScope,
  deviceModel,
  tenantId,
  device,
  siteId,
  groupIds,
  msTeamsAssistedProvisioningEnabled
}: UseRelevantPoliciesProps) => {
  // deviceId, siteId and groupIds are not added since we want to fetch all policies (device, site, group and model) to calculate inheritance
  // deviceId, siteId and groupIds are used later below to filter out only relevant policies
  const variables: PoliciesQueryVariables = {
    tenantId,
    deviceModel
  };

  const { data, fetching } = usePoliciesQuery({
    variables
  });
  const relevantSiteId = device ? device.site?.id : siteId;

  const policies = useMemo(
    () =>
      data?.getPolicies?.filter(policy => {
        const { policyDeploymentScope, policyType, deviceId, siteId, groupId } =
          policy ?? {};

        return (
          policyType === type &&
          policyDeploymentScope &&
          (policyDeploymentScope !== PolicyDeploymentScope.Device ||
            deviceId === device?.id) &&
          (policyDeploymentScope !== PolicyDeploymentScope.Site ||
            siteId === relevantSiteId) &&
          (policyDeploymentScope !== PolicyDeploymentScope.Group ||
            (groupId && groupIds.includes(groupId))) &&
          (INHERITANCE_ORDER[policyScope] >=
            INHERITANCE_ORDER[policyDeploymentScope] ||
            (policyScope === PolicyDeploymentScope.Device &&
              msTeamsAssistedProvisioningEnabled &&
              policy?.policyDeploymentScope ===
                PolicyDeploymentScope.ExternalDevice))
        );
      }) || [],
    [
      data?.getPolicies,
      device?.id,
      groupIds,
      relevantSiteId,
      policyScope,
      type,
      msTeamsAssistedProvisioningEnabled
    ]
  );

  const sortedPolicies = useMemo(() => {
    return policies.sort((a, b) => {
      if (a && b) {
        const priorityA = priorityFromPolicy(a, groupIds);
        const priorityB = priorityFromPolicy(b, groupIds);
        return priorityA - priorityB;
      }
      return 0;
    });
  }, [policies, groupIds]);

  return { policies, sortedPolicies, fetching };
};

const useCatalogModel = ({ device, model }: UseCatalogModelProps) => {
  // if we have device then this query can be skipped
  const { data, fetching } = useHardwareModelsQuery({
    pause: !!device
  });

  const catalogModel = useMemo(() => {
    const catalogModels = data?.hardwareModels ?? [];

    return device?.model?.id
      ? {
          id: device.model.id,
          name: model
        }
      : findCatalogModel(model, catalogModels);
  }, [data?.hardwareModels, device?.model?.id, model]);

  return { catalogModel, fetching };
};

export const useSettingsPolicy = ({
  model,
  tenantId,
  device,
  siteId,
  groupId,
  msTeamsAssistedProvisioningEnabled
}: UseSettingsPolicyProps) => {
  const deviceModel = getDeviceModel(model);
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();

  const {
    data: allGroupsData,
    fetching: fetchingAllGroups
  } = useDeviceGroupsQuery({
    variables: {
      tenantId
    }
  });

  const { catalogModel, fetching: fetchingCatalogModel } = useCatalogModel({
    model,
    device
  });

  const { relevantGroupIds, fetching: fetchingGroups } = useRelevantGroupIds({
    tenantId,
    device,
    groupId
  });

  const relevantSiteName = device?.site?.name || "";
  const deviceName = device?.name || "";
  const {
    policies: relevantPolicies,
    sortedPolicies: sortedRelevantPolicies,
    fetching: fetchingPolicies
  } = useRelevantPolicies({
    type: PolicyType.DeviceSettingsUpdate,
    policyScope,
    deviceModel,
    tenantId,
    device,
    siteId,
    groupIds: relevantGroupIds,
    msTeamsAssistedProvisioningEnabled
  });

  const allGroups = allGroupsData?.getAllGroups.groups?.groups ?? [];

  const inheritance: InheritanceDetails = useInheritance({
    policyScope,
    deviceName,
    siteName: relevantSiteName,
    groupIds: relevantGroupIds,
    relevantPolicies: sortedRelevantPolicies,
    allGroups
  });

  useDispatchInheritance({
    catalogModel,
    inheritance: { ...inheritance }
  });

  let policy: Maybe<PolicyFragment> = useMemo(() => {
    return (
      relevantPolicies.filter(
        policy => policy?.policyDeploymentScope === policyScope
      )[0] ??
      constructNewPolicy(
        deviceModel,
        PolicyType.DeviceSettingsUpdate,
        policyScope,
        tenantId,
        device?.id,
        siteId,
        groupId
      )
    );
  }, [
    relevantPolicies,
    policyScope,
    deviceModel,
    device?.id,
    groupId,
    siteId,
    tenantId
  ]);

  populateDeviceModelUuid(policy, policyScope, device, catalogModel?.id);

  const fetching =
    fetchingPolicies ||
    fetchingCatalogModel ||
    fetchingGroups ||
    fetchingAllGroups;

  return { policy, fetching };
};

export const useSoftwarePolicy = ({
  model,
  tenantId,
  deviceParameters,
  device,
  siteId,
  groupId,
  msTeamsAssistedProvisioningEnabled
}: UseSoftwarePolicyProps) => {
  const deviceModel = getDeviceModel(model);
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();

  const {
    data: allGroupsData,
    fetching: fetchingAllGroups
  } = useDeviceGroupsQuery({
    variables: {
      tenantId
    }
  });

  const { catalogModel, fetching: fetchingCatalogModel } = useCatalogModel({
    model,
    device
  });

  const { relevantGroupIds, fetching: fetchingGroups } = useRelevantGroupIds({
    tenantId,
    device,
    groupId
  });

  const relevantSiteName = device?.site?.name || "";
  const deviceName = device?.name || "";
  const {
    policies: relevantPolicies,
    sortedPolicies: sortedRelevantPolicies,
    fetching: fetchingPolicies
  } = useRelevantPolicies({
    type: PolicyType.DeviceSwUpdate,
    policyScope,
    deviceModel,
    tenantId,
    device,
    siteId,
    groupIds: relevantGroupIds,
    msTeamsAssistedProvisioningEnabled
  });

  const allGroups = allGroupsData?.getAllGroups.groups?.groups ?? [];

  const { inheritance, inheritanceForDisplay } = useSwUpdateInheritance({
    policyScope,
    deviceName,
    siteName: relevantSiteName,
    groupIds: relevantGroupIds,
    relevantPolicies: sortedRelevantPolicies,
    allGroups
  });

  useDispatchInheritance({
    catalogModel,
    inheritance: inheritanceForDisplay
  });

  let policy: Maybe<PolicyFragment> = useMemo(() => {
    return (
      relevantPolicies.filter(
        policy => policy?.policyDeploymentScope === policyScope
      )[0] ??
      constructNewPolicy(
        deviceModel,
        PolicyType.DeviceSwUpdate,
        policyScope,
        tenantId,
        device?.id,
        siteId,
        groupId
      )
    );
  }, [
    relevantPolicies,
    policyScope,
    deviceModel,
    device?.id,
    groupId,
    siteId,
    tenantId
  ]);

  populateDeviceModelUuid(policy, policyScope, device, catalogModel?.id);

  const {
    updateValues,
    updateSettings,
    fetching: fetchingUpdateValuesAndSettings
  } = useSwUpdateValuesAndSettings({
    deviceModel,
    policyScope,
    policy,
    inheritance,
    deviceParameters
  });

  const fetching =
    fetchingPolicies ||
    fetchingUpdateValuesAndSettings ||
    fetchingCatalogModel ||
    fetchingGroups ||
    fetchingAllGroups;

  return { policy, updateSettings, updateValues, fetching };
};
